import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { BodyText, Button, theme, useResponsive } from '@sg/garnish';

export const HomeOrderCanceledBanner = (props: HomeOrderCanceledBannerProps) => {
  const { onPress } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();
  const safeAreaInsets = useSafeAreaInsets();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerXSDynamicStyle = useStyle(
    () => ({
      paddingLeft: Math.max(safeAreaInsets.left, theme.spacing['4']),
      paddingRight: Math.max(safeAreaInsets.right, theme.spacing['4']),
    }),
    [safeAreaInsets.left, safeAreaInsets.right],
  );
  const containerStyles = match([
    [styles.containerXS, containerXSDynamicStyle],
    styles.containerSM,
  ]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <BodyText sizeMatch={['16']}>
        <FormattedMessage {...messages.message} />
      </BodyText>

      <Button
        palette="secondary"
        size="small"
        aria-label={formatMessage(messages.cta)}
        accessibilityRole="link"
        onPress={onPress}
      >
        <FormattedMessage {...messages.cta} />
      </Button>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing['3'],
    backgroundColor: theme.colors.PEACH,
  },
  containerSM: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing['6'],
    padding: theme.spacing['4'],
    backgroundColor: theme.colors.PEACH,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  message: {
    defaultMessage: 'Sorry, your order was canceled.',
    description: 'Home | Order Canceled Banner | Message',
  },
  cta: {
    defaultMessage: 'View order',
    description: 'Home | Order Canceled Banner | CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeOrderCanceledBannerProps = {
  onPress: () => void;
};
